import { kAnchorLinkSelector, kButtonSelector, kCaptchaSelector, kFieldSelector, kLayoutSelector, kPasswordSelector, kSocialSelector } from "@protontech/autofill/constants/selectors";
import { feature, featureCount, featureScaled, featuresProduct, flattenFeatures } from "@protontech/autofill/features/feature";
import type { ComputedFeatures } from "@protontech/autofill/types";
import { getFormAttributes } from "@protontech/autofill/utils/attributes";
import { any } from "@protontech/autofill/utils/combinators";
import { getCCFieldType } from "@protontech/autofill/utils/credit-card";
import { uniqueNodes } from "@protontech/autofill/utils/dom";
import { getAllFieldHaystacks, getAllNodeHaystacks, getFormText, getNearestHeadingsText, getNodeAttributes, getPageDescriptionText } from "@protontech/autofill/utils/extract";
import { safeInt } from "@protontech/autofill/utils/fathom";
import type { HTMLFieldElement } from "@protontech/autofill/utils/field";
import { isBtnCandidate, isEmailCandidate, isInputField, isMFACandidate, isOAuthCandidate, isSelect, isTextarea, isUsernameCandidate, splitFieldsByVisibility } from "@protontech/autofill/utils/field";
import { createInputIterator, getFormComplexity, getFormParent } from "@protontech/autofill/utils/form";
import { getIdentityFieldType } from "@protontech/autofill/utils/identity";
import {
  matchAuthenticator,
  matchLogin,
  matchMFA,
  matchMultiStep,
  matchNewsletter,
  matchOtpOutlier,
  matchOtpOutlierAction,
  matchPasswordConfirmAttr,
  matchPasswordCreateAttr,
  matchPasswordReset,
  matchPasswordResetAttr,
  matchRecovery,
  matchRegister,
  matchRememberMe,
  matchSearchAction,
  matchStepAction,
  matchTOS,
  matchTrouble,
} from "@protontech/autofill/utils/re";
import { sanitizeString } from "@protontech/autofill/utils/text";
import { isVisibleEl, isVisibleField } from "@protontech/autofill/utils/visible";

/* FORM */
export const form = feature("form", {}, (_, fnode) => fnode.element);
export const parent = feature("parent", { form }, (p) => getFormParent(p.form));
export const doc = feature("doc", { form }, (p) => p.form.ownerDocument);

/* FIELDS */
export const inputIterator = feature("inputIterator", {}, (_, fnode) => createInputIterator(fnode.element), { private: true });
export const fields = feature("fields", { form }, (p) => Array.from(p.form.querySelectorAll<HTMLFieldElement>(kFieldSelector)));
export const visibleFields = feature("visibleFields", { fields }, (p) => p.fields.filter(isVisibleField));
export const inputs = feature("inputs", { fields }, (p) => p.fields.filter(isInputField));
export const hidden = feature("hidden", { inputs }, (p) => p.inputs.filter((el) => el.type === "hidden"));
export const visibleInputs = feature("visibleInputs", { visibleFields }, (p) => p.visibleFields.filter(isInputField));
export const fieldsets = feature("fieldsets", { form }, (p) => Array.from(p.form.querySelectorAll<HTMLFieldSetElement>("fieldset")));
export const textareas = feature("textareas", { fields }, (p) => p.fields.filter(isTextarea));
export const selects = feature("selects", { fields }, (p) => p.fields.filter((el, i, arr): el is HTMLSelectElement => isSelect(el) && (arr?.[i + 1] as HTMLInputElement)?.type !== "tel"));

/* When selecting field types we must account for invisible fields in
 * order to support multi-step forms: these forms will generally hide
 * previous inputs (through css or via [type="hidden"]) - when trying
 * to match candidates, it's important not to filter certain invisible
 * fields for the classifier to recognize the underlying patterns */
export const texts = feature("texts", { inputs }, (p) => p.inputs.filter((el) => el.type === "text"));
export const tels = feature("tels", { inputs }, (p) => p.inputs.filter((el) => el.type === "tel"));
export const readOnly = feature("readOnly", { inputs }, (p) => p.inputs.filter((el) => el.readOnly));
export const radios = feature("radios", { inputs }, (p) => p.inputs.filter((el) => el.type === "radio"));
export const checkboxes = feature("checkboxes", { inputs }, (p) => p.inputs.filter((el) => el.type === "checkbox"));
export const numbers = feature("numbers", { inputs }, (p) => p.inputs.filter((el) => el.type === "number"));
export const dates = feature("dates", { inputs }, (p) => p.inputs.filter((el) => el.type === "date"));
export const files = feature("files", { inputs }, (p) => p.inputs.filter((el) => el.type === "file"));

/** NOTE: CC and Identity fields leverage sub-type caching. As such,
 * it's important we run CC type detection **prior* to identities to
 * avoid overriding CC sub-types for ambiguous identity fields */
export const ccInputs = feature("ccInputs", { inputs }, (p) => p.inputs.filter(getCCFieldType));
export const ccSelects = feature("ccSelects", { selects }, (p) => p.selects.filter(getCCFieldType));
export const ccs = feature("ccs", { ccInputs, ccSelects }, (p) => (p.ccInputs as HTMLElement[]).concat(p.ccSelects));
export const identities = feature("identities", { inputs, ccs }, (p) => p.inputs.filter((el) => getIdentityFieldType(el) !== undefined));

export const required = feature("required", { visibleInputs }, (p) => p.visibleInputs.filter((el) => el.required || el.ariaRequired));
export const disabled = feature("disabled", { visibleInputs }, (p) => p.visibleInputs.filter((el) => el.disabled || el.ariaDisabled));
export const autofocused = feature("autofocused", { visibleInputs }, (p) => p.visibleInputs.find((el) => el.matches("input[autofocus]:first-of-type")));

export const usernames = feature("usernames", { inputs }, (p) => p.inputs.filter(isUsernameCandidate));
export const emails = feature("emails", { inputs }, (p) => p.inputs.filter(isEmailCandidate));
export const identifiers = feature("identifiers", { usernames, emails }, (p) => uniqueNodes(p.usernames, p.emails));
export const identifiersByVisibility = feature("identifiersByVisibility", { identifiers }, (p) => splitFieldsByVisibility(p.identifiers));
export const visibleIdentifiers = feature("visibleIdentifiers", { identifiersByVisibility }, (p) => p.identifiersByVisibility[0]);
export const hiddenIdentifiers = feature("hiddenIdentifiers", { identifiersByVisibility }, (p) => p.identifiersByVisibility[1]);

export const passwords = feature("passwords", { inputs }, (p) => p.inputs.filter((el) => el.matches(kPasswordSelector)));
export const passwordsByVisibility = feature("passwordsByVisibility", { passwords }, (p) => splitFieldsByVisibility(p.passwords));
export const visiblePasswords = feature("visiblePasswords", { passwordsByVisibility }, (p) => p.passwordsByVisibility[0]);
export const hiddenPasswords = feature("hiddenPasswords", { passwordsByVisibility }, (p) => p.passwordsByVisibility[1]);

export const mfaInputs = feature("mfaInputs", { inputs }, (p) => p.inputs.filter(isMFACandidate));

/* lookup the following elements outside the form */
export const captchas = feature("captchas", { parent }, (p) => Array.from(p.parent.querySelectorAll<HTMLElement>(kCaptchaSelector)));
export const socials = feature("socials", { parent }, (p) => Array.from(p.parent.querySelectorAll<HTMLElement>(kSocialSelector)));

export const submits = feature("submits", { visibleFields }, (p) => p.visibleFields.filter((el) => el?.type === "submit"));
export const btns = feature("btns", { form }, (p) => Array.from(p.form.querySelectorAll<HTMLElement>(kButtonSelector)).filter(isVisibleEl));
export const btnsTypeSubmit = feature("btnsTypeSubmit", { btns }, (p) => p.btns.filter((el) => el.matches('[type="submit"]')));
export const candidateBtns = feature("candidateBtns", { btns }, (p) => p.btns.filter(isBtnCandidate));
export const submitBtns = feature("submitBtns", { btns, btnsTypeSubmit, candidateBtns }, (p) => (p.btnsTypeSubmit.length > 0 ? p.btnsTypeSubmit : p.candidateBtns));

export const anchors = feature("anchors", { form }, (p) => Array.from(p.form.querySelectorAll<HTMLElement>(kAnchorLinkSelector)).filter(isVisibleEl));
export const oauths = feature("oauths", { socials, candidateBtns }, (p) => p.socials.concat(p.candidateBtns).filter(isOAuthCandidate));
export const layouts = feature("layouts", { form }, (p) => Array.from(p.form.querySelectorAll<HTMLElement>(kLayoutSelector)));

/* AUTOFOCUSED */
export const autofocusedIsIdentifier = feature("autofocusedIsIdentifier", { autofocused, visibleIdentifiers }, (p) => Boolean(p.autofocused && p.visibleIdentifiers.includes(p.autofocused)));
export const autofocusedIsPassword = feature("autofocusedIsPassword", { autofocused, visiblePasswords }, (p) => Boolean(p.autofocused && p.visiblePasswords.includes(p.autofocused)));

/* HAYSTACKS */
export const pageDescriptionText = feature("pageDescriptionText", { doc }, (p) => getPageDescriptionText(p.doc));
export const nearestHeadingsText = feature("nearestHeadingsText", { form }, (p) => getNearestHeadingsText(p.form));
export const formTextAttrText = feature("formTextAttrText", { form }, (p) => getFormText(p.form));
export const formAttributes = feature("formAttributes", { form }, (p) => getFormAttributes(p.form));
export const pwHaystack = feature("pwHaystack", { passwords }, (p) => p.passwords.flatMap(getAllFieldHaystacks));
export const identifierHaystack = feature("identifierHaystack", { visibleIdentifiers }, (p) => p.visibleIdentifiers.flatMap(getAllFieldHaystacks));
export const btnHaystack = feature("btnHaystack", { candidateBtns }, (p) => p.candidateBtns.flatMap(getAllFieldHaystacks));
export const submitBtnHaystack = feature("submitBtnHaystack", { submitBtns }, (p) => p.submitBtns.flatMap(getAllFieldHaystacks));
export const checkboxesHaystack = feature("checkboxesHaystack", { checkboxes }, (p) => p.checkboxes.flatMap(getAllFieldHaystacks));
export const anchorsHaystack = feature("anchorsHaystack", { anchors }, (p) => p.anchors.flatMap(getAllNodeHaystacks));
export const mfaInputsHaystack = feature("mfaInputsHaystack", { mfaInputs }, (p) => p.mfaInputs.flatMap(getAllFieldHaystacks));
export const layoutHaystack = feature("layoutHaystack", { layouts }, (p) => p.layouts.map(getNodeAttributes));
export const formTextHaystack = feature("formTextHaystack", { formTextAttrText, nearestHeadingsText }, (p) => [p.formTextAttrText, p.nearestHeadingsText]);

/* LOGIN FEATURES */
export const pageLogin = feature("pageLogin", { pageDescriptionText }, (p) => matchLogin(p.pageDescriptionText));
export const formTextLogin = feature("formTextLogin", { formTextAttrText }, (p) => matchLogin(p.formTextAttrText));
export const formAttrsLogin = feature("formAttrsLogin", { formAttributes }, (p) => any(matchLogin)(p.formAttributes));
export const headingsLogin = feature("headingsLogin", { nearestHeadingsText }, (p) => matchLogin(p.nearestHeadingsText));
export const layoutLogin = feature("layoutLogin", { layoutHaystack }, (p) => any(matchLogin)(p.layoutHaystack));
export const rememberMeCheckbox = feature("rememberMeCheckbox", { checkboxesHaystack }, (p) => any(matchRememberMe)(p.checkboxesHaystack));
export const submitLogin = feature("submitLogin", { submitBtnHaystack }, (p) => any(matchLogin)(p.submitBtnHaystack));
export const troubleLink = feature("troubleLink", { anchorsHaystack }, (p) => any(matchTrouble)(p.anchorsHaystack));

/* REGISTER FEATURES */
export const pageRegister = feature("pageRegister", { pageDescriptionText }, (p) => matchRegister(p.pageDescriptionText));
export const formTextRegister = feature("formTextRegister", { formTextAttrText }, (p) => matchRegister(p.formTextAttrText));
export const formAttrsRegister = feature("formAttrsRegister", { formAttributes }, (p) => any(matchRegister)(p.formAttributes));
export const headingsRegister = feature("headingsRegister", { nearestHeadingsText }, (p) => matchRegister(p.nearestHeadingsText));
export const layoutRegister = feature("layoutRegister", { layoutHaystack }, (p) => any(matchRegister)(p.layoutHaystack));
export const TOSRef = feature("TOSRef", { checkboxesHaystack, anchorsHaystack }, (p) => any(matchTOS)(p.checkboxesHaystack.concat(p.anchorsHaystack)));
export const submitRegister = feature("submitRegister", { submitBtnHaystack }, (p) => any(matchRegister)(p.submitBtnHaystack));
export const pwNewRegister = feature("pwNewRegister", { pwHaystack }, (p) => any(matchPasswordCreateAttr)(p.pwHaystack));
export const pwConfirmRegister = feature("pwConfirmRegister", { pwHaystack }, (p) => any(matchPasswordConfirmAttr)(p.pwHaystack));

/* MULTI-STEP FEATURES */
export const buttonMultistep = feature("buttonMultistep", { submitBtnHaystack }, (p) => any(matchStepAction)(p.submitBtnHaystack));
export const headingsMultistep = feature("headingsMultistep", { nearestHeadingsText }, (p) => matchMultiStep(p.nearestHeadingsText));
export const multistepForm = feature("multistepForm", { buttonMultistep, headingsMultistep }, (p) => p.buttonMultistep || p.headingsMultistep);

/* MULTI-AUTH FORM FEATURES */
export const pageMultiAuth = feature("pageMultiAuth", { pageLogin, pageRegister }, (p) => p.pageLogin && p.pageRegister);
export const submitMultiAuth = feature("submitMultiAuth", { submitRegister, submitLogin, buttonMultistep }, (p) => p.submitRegister && (p.submitLogin || p.buttonMultistep));
export const headingsMultiAuth = feature("headingsMultiAuth", { headingsLogin, headingsRegister }, (p) => p.headingsLogin && p.headingsRegister);
export const multiAuthForm = feature("multiAuthForm", { pageMultiAuth, headingsMultiAuth, submitMultiAuth }, (p) => p.pageMultiAuth || p.headingsMultiAuth || p.submitMultiAuth);

/* PASSWORD RESET FEATURES */
export const pagePwReset = feature("pagePwReset", { pageDescriptionText }, (p) => matchPasswordReset(p.pageDescriptionText));
export const formTextPwReset = feature("formTextPwReset", { formTextAttrText }, (p) => matchPasswordReset(p.formTextAttrText));
export const formAttrsPwReset = feature("formAttrsPwReset", { formAttributes }, (p) => any(matchPasswordResetAttr)(p.formAttributes));
export const headingsPwReset = feature("headingsPwReset", { nearestHeadingsText }, (p) => matchPasswordReset(p.nearestHeadingsText));
export const layoutPwReset = feature("layoutPwReset", { layoutHaystack }, (p) => any(matchPasswordResetAttr)(p.layoutHaystack));

/* RECOVERY FEATURES */
export const pageRecovery = feature("pageRecovery", { pageDescriptionText }, (p) => matchRecovery(p.pageDescriptionText));
export const formTextRecovery = feature("formTextRecovery", { formTextAttrText }, (p) => matchRecovery(p.formTextAttrText));
export const formAttrsRecovery = feature("formAttrsRecovery", { formAttributes }, (p) => any(matchRecovery)(p.formAttributes));
export const headingsRecovery = feature("headingsRecovery", { nearestHeadingsText }, (p) => matchRecovery(p.nearestHeadingsText));
export const layoutRecovery = feature("layoutRecovery", { layoutHaystack }, (p) => any(matchRecovery)(p.layoutHaystack));
export const submitRecovery = feature("submitRecovery", { submitBtnHaystack }, (p) => any(matchRecovery)(p.submitBtnHaystack));
export const identifierRecovery = feature("identifierRecovery", { identifierHaystack }, (p) => any(matchRecovery)(p.identifierHaystack));

/* MFA FEATURES */
export const inputsMFA = feature("inputsMFA", { mfaInputs, mfaInputsHaystack }, (p) => p.mfaInputs.some(({ autocomplete }) => autocomplete.includes("one-time-code")) || any(matchMFA)(p.mfaInputsHaystack));

export const formAttrsMFA = feature("formAttrsMFA", { formAttributes }, (p) => any(matchMFA)(p.formAttributes));
export const formTextMFA = feature("formTextMFA", { formTextHaystack }, (p) => any(matchMFA)(p.formTextHaystack));
export const maybeMFA = feature("maybeMFA", { formTextMFA, formAttrsMFA, inputsMFA }, (p) => p.inputsMFA || p.formAttrsMFA || p.formTextMFA);
export const haystackMFA = feature("haystackMFA", { formTextHaystack, maybeMFA, form }, (p) => [...p.formTextHaystack, p.maybeMFA ? sanitizeString(p.form.innerText) : ""]);
export const formTextAuthenticator = feature("formTextAuthenticator", { haystackMFA }, (p) => any(matchAuthenticator)(p.haystackMFA), { private: true });
export const headingsOTPOutlier = feature("headingsOTPOutlier", { nearestHeadingsText }, (p) => matchOtpOutlier(p.nearestHeadingsText), { private: true });
export const formOTPOutlier = feature("formOTPOutlier", { formAttributes }, (p) => any(matchOtpOutlier)(p.formAttributes), { private: true });
export const linkOTPOutlier = feature("linkOTPOutlier", { anchorsHaystack, btnHaystack }, (p) => any(matchOtpOutlierAction)(p.anchorsHaystack.concat(p.btnHaystack)), { private: true });
export const formMFA = feature("formMFA", { formTextMFA, formAttrsMFA }, (p) => p.formTextMFA || p.formAttrsMFA, { private: true });
export const formInputMFACandidates = feature("formInputMFACandidates", { visibleInputs }, (p) => p.visibleInputs.filter(isMFACandidate).length, { private: true });

/* OUTLIER FEATURES */
export const newsletterForm = feature("newsletterForm", { formTextHaystack }, (p) => any(matchNewsletter)(p.formTextHaystack));
export const searchForm = feature("searchForm", { formTextHaystack }, (p) => any(matchSearchAction)(p.formTextHaystack));

/* FORM COMPLEXITY */
export const formComplexity = featureScaled(
  feature("formComplexity", { form, fields, visibleFields, hidden, anchors, candidateBtns }, (p) =>
    getFormComplexity(p.form, {
      fields: p.fields,
      visibleFields: p.visibleFields.length,
      hiddenFields: p.hidden.length,
      nonVisibleFields: p.fields.length - p.visibleFields.length - p.hidden.length,
      anchors: p.anchors.length,
      buttons: p.candidateBtns.length,
    }),
  ),
  0,
  50,
);

/* FIELD COUNTS */
export const visibleFieldsCount = featureScaled(featureCount(visibleFields), 1, 5);
export const visibleInputsCount = featureScaled(featureCount(visibleInputs), 1, 5);
export const fieldsetsCount = featureScaled(featureCount(fieldsets), 1, 5);
export const textsCount = featureScaled(featureCount(texts), 0, 3);
export const readOnlyCount = featureScaled(featureCount(readOnly), 0, 3);
export const textareasCount = featureScaled(featureCount(textareas), 0, 2);
export const selectsCount = featureScaled(featureCount(selects), 0, 5);
export const disabledCount = featureScaled(featureCount(disabled), 0, 5);
export const radiosCount = featureScaled(featureCount(radios), 0, 5);
export const visibleIdentifiersCount = featureScaled(featureCount(visibleIdentifiers), 0, 3);
export const hiddenIdentifiersCount = featureScaled(featureCount(hiddenIdentifiers), 0, 2);
export const hiddenCount = featureScaled(featureCount(hidden), 0, 5);
export const visiblePasswordsCount = featureScaled(featureCount(visiblePasswords), 0, 2);
export const hiddenPasswordsCount = featureScaled(featureCount(hiddenPasswords), 0, 2);
export const usernamesCount = featureScaled(featureCount(usernames), 0, 2);
export const emailsCount = featureScaled(featureCount(emails), 0, 2);
export const submitsCount = featureScaled(featureCount(submits), 0, 2);
export const identitiesCount = featureScaled(featureCount(identities), 0, 5);
export const ccsCount = featureScaled(featureCount(ccs), 0, 5);

/* FIELD PRESENCE */
export const hasTels = feature("hasTels", { tels }, (p) => p.tels.length > 0);
export const hasOAuth = feature("hasOAuth", { oauths }, (p) => p.oauths.length > 0);
export const hasCaptchas = feature("hasCaptchas", { captchas }, (p) => p.captchas.length > 0);
export const hasFiles = feature("hasFiles", { files }, (p) => p.files.length > 0);
export const hasDate = feature("hasDate", { dates }, (p) => p.dates.length > 0);
export const hasNumber = feature("hasNumber", { numbers }, (p) => p.numbers.length > 0);

/* FIELD DISTRIBUTION MATCHES */
export const oneVisibleField = feature("oneVisibleField", { visibleInputs }, (p) => p.visibleInputs.length === 1);
export const twoVisibleFields = feature("twoVisibleFields", { visibleInputs }, (p) => p.visibleInputs.length === 2);
export const threeOrMoreVisibleFields = feature("threeOrMoreVisibleFields", { visibleInputs }, (p) => p.visibleInputs.length >= 3);
export const noPasswords = feature("noPasswords", { visiblePasswords }, (p) => p.visiblePasswords.length === 0);
export const onePassword = feature("onePassword", { visiblePasswords }, (p) => p.visiblePasswords.length === 1);
export const twoPasswords = feature("twoPasswords", { visiblePasswords }, (p) => p.visiblePasswords.length === 2);
export const threeOrMorePasswords = feature("threeOrMorePasswords", { visiblePasswords }, (p) => p.visiblePasswords.length >= 3);
export const noIdentifiers = feature("noIdentifiers", { visibleIdentifiers }, (p) => p.visibleIdentifiers.length === 0);
export const oneIdentifier = feature("oneIdentifier", { visibleIdentifiers }, (p) => p.visibleIdentifiers.length === 1);
export const twoIdentifiers = feature("twoIdentifiers", { visibleIdentifiers }, (p) => p.visibleIdentifiers.length === 2);
export const threeOrMoreIdentifiers = feature("threeOrMoreIdentifiers", { visibleIdentifiers }, (p) => p.visibleIdentifiers.length >= 3);

/* RATIOS */
export const inputRatio = feature("inputRatio", { inputs, fields }, (p) => safeInt(p.inputs.length / p.fields.length));
export const hiddenRatio = feature("hiddenRatio", { hidden, fields }, (p) => safeInt(p.hidden.length / p.fields.length));
export const visibleRatio = feature("visibleRatio", { visibleInputs, fields }, (p) => safeInt(p.visibleInputs.length / p.fields.length));
export const checkboxRatio = feature("checkboxRatio", { checkboxes, fields }, (p) => safeInt(p.checkboxes.length / p.fields.length));
export const emailRatio = feature("emailRatio", { emails, fields }, (p) => safeInt(p.emails.length / p.fields.length));
export const usernameRatio = feature("usernameRatio", { usernames, fields }, (p) => safeInt(p.usernames.length / p.fields.length));
export const hiddenIdentifierRatio = feature("hiddenIdentifierRatio", { hiddenIdentifiers, identifiers }, (p) => safeInt(p.hiddenIdentifiers.length / p.identifiers.length));
export const hiddenPasswordRatio = feature("hiddenPasswordRatio", { hiddenPasswords, passwords }, (p) => safeInt(p.hiddenPasswords.length / p.passwords.length));
export const identifierRatio = feature("identifierRatio", { visibleIdentifiers, visibleFields }, (p) => safeInt(p.visibleIdentifiers.length / p.visibleFields.length));
export const passwordRatio = feature("passwordRatio", { visiblePasswords, visibleFields }, (p) => safeInt(p.visiblePasswords.length / p.visibleFields.length));
export const requiredRatio = feature("requiredRatio", { required, visibleFields }, (p) => safeInt(p.required.length / p.visibleFields.length));
export const disabledRatio = feature("disabledRatio", { disabled, visibleFields }, (p) => safeInt(p.disabled.length / p.visibleFields.length));

/* COMBINED */
export const multistepForm_multiAuthForm = featuresProduct(multistepForm, multiAuthForm);

export const visibleRatio_visibleFieldsCount = featuresProduct(visibleRatio, visibleFieldsCount);
export const visibleRatio_visibleIdentifiersCount = featuresProduct(visibleRatio, visibleIdentifiersCount);
export const visibleRatio_visiblePasswordsCount = featuresProduct(visibleRatio, visiblePasswordsCount);
export const visibleRatio_hiddenIdentifiersCount = featuresProduct(visibleRatio, hiddenIdentifiersCount);
export const visibleRatio_hiddenPasswordsCount = featuresProduct(visibleRatio, hiddenPasswordsCount);
export const visibleRatio_multiAuthForm = featuresProduct(visibleRatio, multiAuthForm);
export const visibleRatio_multistepForm = featuresProduct(visibleRatio, multistepForm);

export const identifierRatio_visibleFieldsCount = featuresProduct(identifierRatio, visibleFieldsCount);
export const identifierRatio_visibleIdentifiersCount = featuresProduct(identifierRatio, visibleIdentifiersCount);
export const identifierRatio_visiblePasswordsCount = featuresProduct(identifierRatio, visiblePasswordsCount);
export const identifierRatio_hiddenIdentifiersCount = featuresProduct(identifierRatio, hiddenIdentifiersCount);
export const identifierRatio_hiddenPasswordsCount = featuresProduct(identifierRatio, hiddenPasswordsCount);
export const identifierRatio_multiAuthForm = featuresProduct(identifierRatio, multiAuthForm);
export const identifierRatio_multistepForm = featuresProduct(identifierRatio, multistepForm);

export const passwordRatio_visibleFieldsCount = featuresProduct(passwordRatio, visibleFieldsCount);
export const passwordRatio_visibleIdentifiersCount = featuresProduct(passwordRatio, visibleIdentifiersCount);
export const passwordRatio_visiblePasswordsCount = featuresProduct(passwordRatio, visiblePasswordsCount);
export const passwordRatio_hiddenIdentifiersCount = featuresProduct(passwordRatio, hiddenIdentifiersCount);
export const passwordRatio_hiddenPasswordsCount = featuresProduct(passwordRatio, hiddenPasswordsCount);
export const passwordRatio_multiAuthForm = featuresProduct(passwordRatio, multiAuthForm);
export const passwordRatio_multistepForm = featuresProduct(passwordRatio, multistepForm);

export const requiredRatio_visibleFieldsCount = featuresProduct(requiredRatio, visibleFieldsCount);
export const requiredRatio_visibleIdentifiersCount = featuresProduct(requiredRatio, visibleIdentifiersCount);
export const requiredRatio_visiblePasswordsCount = featuresProduct(requiredRatio, visiblePasswordsCount);
export const requiredRatio_hiddenIdentifiersCount = featuresProduct(requiredRatio, hiddenIdentifiersCount);
export const requiredRatio_hiddenPasswordsCount = featuresProduct(requiredRatio, hiddenPasswordsCount);
export const requiredRatio_multiAuthForm = featuresProduct(requiredRatio, multiAuthForm);
export const requiredRatio_multistepForm = featuresProduct(requiredRatio, multistepForm);

export const formFeatures = flattenFeatures({
  // ─── Private Features ───────────────────
  formInputMFACandidates,
  formMFA,
  formOTPOutlier,
  formTextAuthenticator,
  headingsOTPOutlier,
  inputIterator,
  linkOTPOutlier,
  // ────────────────────────────────────────

  visibleFieldsCount,
  visibleInputsCount,
  fieldsetsCount,
  textsCount,
  textareasCount,
  selectsCount,
  disabledCount,
  radiosCount,
  readOnlyCount,

  formComplexity,

  visibleIdentifiersCount,
  hiddenIdentifiersCount,
  usernamesCount,
  emailsCount,
  hiddenCount,
  hiddenPasswordsCount,

  submitsCount,

  identitiesCount,
  ccsCount,

  hasTels,
  hasOAuth,
  hasCaptchas,
  hasFiles,
  hasDate,
  hasNumber,

  oneVisibleField,
  twoVisibleFields,
  threeOrMoreVisibleFields,

  noPasswords,
  onePassword,
  twoPasswords,
  threeOrMorePasswords,

  noIdentifiers,
  oneIdentifier,
  twoIdentifiers,
  threeOrMoreIdentifiers,

  autofocusedIsIdentifier,
  autofocusedIsPassword,

  visibleRatio,
  inputRatio,
  hiddenRatio,
  identifierRatio,
  emailRatio,
  usernameRatio,
  passwordRatio,
  disabledRatio,
  requiredRatio,
  checkboxRatio,
  hiddenIdentifierRatio,
  hiddenPasswordRatio,

  pageLogin,
  formTextLogin,
  formAttrsLogin,
  headingsLogin,
  layoutLogin,
  rememberMeCheckbox,
  troubleLink,
  submitLogin,

  pageRegister,
  formTextRegister,
  formAttrsRegister,
  headingsRegister,
  layoutRegister,
  pwNewRegister,
  pwConfirmRegister,
  submitRegister,
  TOSRef,

  pagePwReset,
  formTextPwReset,
  formAttrsPwReset,
  headingsPwReset,
  layoutPwReset,

  pageRecovery,
  formTextRecovery,
  formAttrsRecovery,
  headingsRecovery,
  layoutRecovery,
  identifierRecovery,
  submitRecovery,

  formTextMFA,
  formAttrsMFA,
  inputsMFA,

  newsletterForm,
  searchForm,

  multistepForm,
  multiAuthForm,

  multistepForm_multiAuthForm,

  visibleRatio_visibleFieldsCount,
  visibleRatio_visibleIdentifiersCount,
  visibleRatio_visiblePasswordsCount,
  visibleRatio_hiddenIdentifiersCount,
  visibleRatio_hiddenPasswordsCount,
  visibleRatio_multiAuthForm,
  visibleRatio_multistepForm,

  identifierRatio_visibleFieldsCount,
  identifierRatio_visibleIdentifiersCount,
  identifierRatio_visiblePasswordsCount,
  identifierRatio_hiddenIdentifiersCount,
  identifierRatio_hiddenPasswordsCount,
  identifierRatio_multiAuthForm,
  identifierRatio_multistepForm,

  passwordRatio_visibleFieldsCount,
  passwordRatio_visibleIdentifiersCount,
  passwordRatio_visiblePasswordsCount,
  passwordRatio_hiddenIdentifiersCount,
  passwordRatio_hiddenPasswordsCount,
  passwordRatio_multiAuthForm,
  passwordRatio_multistepForm,

  requiredRatio_visibleFieldsCount,
  requiredRatio_visibleIdentifiersCount,
  requiredRatio_visiblePasswordsCount,
  requiredRatio_hiddenIdentifiersCount,
  requiredRatio_hiddenPasswordsCount,
  requiredRatio_multiAuthForm,
  requiredRatio_multistepForm,
});

export type FormFeatures = ComputedFeatures<typeof formFeatures>;
